import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@features/auth/store/authStore';
import { apiClient } from '@lib/api/client';
import { captureScreenMap } from '../lib/captureScreenMap';

export interface SupportStep {
  action: string;
  target?: string;
  label?: string;
}

export interface SupportHelp {
  loading: boolean;
  reply: string | null;
  steps: SupportStep[];
  failed: boolean;
  // Id de la interacción: el panel de feedback ("¿te sirvió?") lo referencia.
  interactionId: string | null;
  // Id de sesión: la guía iterativa lo usa para pedir los próximos pasos.
  sessionId: string | null;
}

interface Params {
  enabled: boolean; // normalmente: el modal está abierto y hay un error
  message: string; // descripción del error / acción (lo que se le manda al agente)
  errorCodes?: string[]; // códigos (ej. DGI) para dar más contexto
  screen?: string; // pantalla/acción donde ocurrió
  /** XML del documento, si el caso es sobre una factura concreta (ver ReportErrorArgs). */
  invoiceXml?: string;
  /** Payload JSON del documento, cuando no hay XML (ver ReportErrorArgs). */
  invoicePayload?: unknown;
  /** Respuesta cruda de la DGI, con la lista completa de hallazgos (ver ReportErrorArgs). */
  dgiResponse?: string;
  /** Identificación de la factura (número, CUFE, estado). */
  invoiceRef?: { number?: string; cufe?: string; status?: string };
}

/**
 * Tope del XML que mandamos. Una FE ronda los 10-30 KB, pero una con cientos de
 * items puede irse muy arriba y el XML viaja dentro del prompt del modelo. Si se
 * pasa, mejor no mandarlo que romper la consulta entera: el agente igual puede
 * trabajar con el codigo de rechazo.
 */
const MAX_XML_CHARS = 120_000;

/**
 * Consulta al agente de soporte (vía el proxy /api/support/report) para obtener
 * una explicación + pasos sugeridos ante un error. Se dispara solo cuando cambia
 * el error (no en cada render). La compañía activa se adjunta automáticamente.
 */
export function useSupportHelp({
  enabled,
  message,
  errorCodes,
  screen,
  invoiceXml,
  invoicePayload,
  dgiResponse,
  invoiceRef,
}: Params): SupportHelp {
  // Leemos la compañía del cache (sin disparar fetch ni acoplar auth).
  const queryClient = useQueryClient();
  /*
   * Tenant activo. Un mismo cliente puede tener decenas de companias, y varios
   * tenants distintos comparten nombres de compania parecidos: sin el tenant, en
   * el panel del agente no se puede saber de que instalacion vino un caso.
   */
  const tenantInfo = useAuthStore((s) => s.tenantInfo);
  const [state, setState] = useState<SupportHelp>({
    loading: false,
    reply: null,
    steps: [],
    failed: false,
    interactionId: null,
    sessionId: null,
  });

  // Clave estable: re-consulta solo si cambia el error (no por la compañía/render).
  const key = enabled && message ? `${screen || ''}|${message}|${(errorCodes || []).join(',')}` : '';

  useEffect(() => {
    if (!key) {
      setState({ loading: false, reply: null, steps: [], failed: false, interactionId: null, sessionId: null });
      return;
    }
    let alive = true;
    setState({ loading: true, reply: null, steps: [], failed: false, interactionId: null, sessionId: null });
    const sc: any = (queryClient.getQueryData(['company', 'selected']) as any)?.company;
    apiClient
      .post(
        '/support/report',
        {
          message,
          context: {
            screen,
            action: screen,
            error_codes: errorCodes,
            // Mapa de la pantalla actual: el agente targetea por `ref` exacto.
            screen_elements: captureScreenMap(),
            company_name: sc?.name || sc?.fullName || undefined,
            company_legal_name: sc?.fullName || undefined,
            company_ruc: sc?.ruc || sc?.numberId || undefined,
            company_dv: sc?.dv || undefined,
            company_id: sc?.id || undefined,
            tenant_id: tenantInfo?.id || undefined,
            tenant_identifier: tenantInfo?.identifier || undefined,
            tenant_name: tenantInfo?.name || undefined,
            // Factura del caso: el agente pasa el XML por `validate_fe_xml` (XSD
            // oficial de la DGI) y asi señala el campo exacto, no solo el codigo.
            invoice_number: invoiceRef?.number || undefined,
            invoice_cufe: invoiceRef?.cufe || undefined,
            invoice_status: invoiceRef?.status || undefined,
            invoice_xml:
              invoiceXml && invoiceXml.length <= MAX_XML_CHARS ? invoiceXml : undefined,
            invoice_payload: invoicePayload || undefined,
            // Respuesta cruda de la DGI: trae gResProcList, que es lo unico que
            // permite ver TODOS los hallazgos y agrupar los repetidos por item.
            dgi_response:
              dgiResponse && dgiResponse.length <= MAX_XML_CHARS ? dgiResponse : undefined,
          },
        },
        { timeout: 130000 }
      )
      .then(({ data }) => {
        if (alive)
          setState({
            loading: false,
            reply: data?.reply ?? null,
            steps: data?.steps ?? [],
            failed: false,
            interactionId: data?.interaction_id ?? null,
            sessionId: data?.session_id ?? null,
          });
      })
      .catch(() => {
        if (alive) setState({ loading: false, reply: null, steps: [], failed: true, interactionId: null, sessionId: null });
      });
    return () => {
      alive = false;
    };
    // Intencional: dependemos de `key` (el error) para no re-consultar en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
